import { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, X } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;
let groupsPromise = null;

export const fetchDiseaseGroups = (force = false) => {
  if (force || !groupsPromise) {
    groupsPromise = axios.get(`${API_URL}/api/config/disease-groups`, { withCredentials: true })
      .then(r => r.data || []).catch(() => { groupsPromise = null; return []; });
  }
  return groupsPromise;
};

export function useDiseaseGroups() {
  const [groups, setGroups] = useState([]);
  useEffect(() => { fetchDiseaseGroups().then(setGroups); }, []);
  return groups;
}

export const groupName = (g, lang) => (lang === 'es' ? g.name_es : (g.name_en || g.name_es));

// Filas actuales; los datos antiguos {value:'si', obs} pasan a una fila sin grupo.
export const toItems = (cur) => {
  if (Array.isArray(cur?.items) && cur.items.length) return cur.items;
  return [{ group: '', obs: cur?.obs || '' }];
};

// Devuelve el motivo por el que la respuesta no es válida, o null.
export const groupItemsProblem = (cur, field, lang) => {
  if (!cur || typeof cur !== 'object' || cur.value !== 'si') return null;
  const items = toItems(cur);
  if (items.some(it => !it.group)) return lang === 'es' ? 'falta el grupo de enfermedad' : 'missing disease group';
  if (new Set(items.map(it => it.group)).size !== items.length) return lang === 'es' ? 'grupo repetido' : 'duplicated group';
  if (field.obs_required !== false && items.some(it => !(it.obs || '').trim())) return lang === 'es' ? 'falta la observación' : 'missing note';
  return null;
};

function GroupRow({ it, idx, groups, usedIds, lang, readOnly, obsReq, fk, onChange, onRemove, canRemove }) {
  const visible = groups.filter(g => g.active !== false || g.id === it.group);
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)_auto] gap-2 items-start" data-testid={`field-${fk}-row-${idx}`}>
      <Select value={it.group || ''} onValueChange={v => onChange({ ...it, group: v })} disabled={readOnly}>
        <SelectTrigger data-testid={`field-${fk}-group-${idx}`} className={`bg-white border-stone-200 ${!it.group ? 'border-amber-400' : ''}`}>
          <SelectValue placeholder={lang === 'es' ? 'Grupo de enfermedad...' : 'Disease group...'} />
        </SelectTrigger>
        <SelectContent>
          {visible.map(g => (
            <SelectItem key={g.id} value={g.id} disabled={usedIds.includes(g.id) && g.id !== it.group}>{groupName(g, lang)}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Textarea data-testid={`field-${fk}-obs-${idx}`} value={it.obs || ''} readOnly={readOnly} onChange={e => onChange({ ...it, obs: e.target.value })}
        className={`bg-white border-stone-200 min-h-[40px] h-10 ${obsReq && !(it.obs || '').trim() ? 'border-amber-400' : ''}`} />
      <Button type="button" variant="ghost" size="icon" className="h-9 w-9 text-stone-400 hover:text-red-500" disabled={readOnly || !canRemove}
        onClick={onRemove} data-testid={`field-${fk}-remove-${idx}`}><X size={14} /></Button>
    </div>
  );
}

export function BooleanObsGroups({ field, cur, lang, readOnly, onChange }) {
  const groups = useDiseaseGroups();
  const items = toItems(cur);
  const obsReq = field.obs_required !== false;
  const fk = field.field_key;
  const obsLabel = (lang === 'es' ? field.obs_label_es : field.obs_label_en) || (lang === 'es' ? 'Observaciones' : 'Notes');
  const setItems = (next) => onChange({ value: 'si', obs: '', items: next });
  const usedIds = items.map(it => it.group).filter(Boolean);
  return (
    <div className="space-y-2 md:col-span-2" data-testid={`field-${fk}-groups`}>
      <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)_auto] gap-2">
        <Label className="text-xs font-medium text-stone-600">{lang === 'es' ? 'Grupo de enfermedad' : 'Disease group'}<span className="text-red-500 ml-1">*</span></Label>
        <Label className="text-xs font-medium text-stone-600">{obsLabel}{obsReq && <span className="text-red-500 ml-1">*</span>}</Label>
        <span className="w-9" />
      </div>
      {items.map((it, idx) => (
        <GroupRow key={idx} it={it} idx={idx} groups={groups} usedIds={usedIds} lang={lang} readOnly={readOnly} obsReq={obsReq} fk={fk}
          canRemove={items.length > 1}
          onChange={nv => setItems(items.map((x, i) => (i === idx ? nv : x)))}
          onRemove={() => setItems(items.filter((_, i) => i !== idx))} />
      ))}
      {!readOnly && (
        <Button type="button" variant="outline" size="sm" className="h-8 gap-1.5 text-xs rounded-sm border-[#1f3e80]/30 text-[#1f3e80]"
          disabled={usedIds.length >= groups.filter(g => g.active !== false).length}
          onClick={() => setItems([...items, { group: '', obs: '' }])} data-testid={`field-${fk}-add-row`}>
          <Plus size={13} />{lang === 'es' ? 'Añadir patología' : 'Add condition'}
        </Button>
      )}
    </div>
  );
}
